import { ErrorHandler, Injectable, Inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { StackFrame, Transport, BaseTransportOptions } from '@sentry/core';
import { noSentryErrorMessage } from '../errors';
import { Panel } from 'app/utils/panel';
import { AuthState } from 'app/services/auth-state';
import { TranslateService } from 'app/utils/translate/translate.service';
import { ConfigRef } from 'app/core/config-ref';
import { AppState } from 'app/core/app.service';

const URL_FOR_ASSETS = 'http://10.192.48.25:8000/fs/assets-by-version';

@Injectable()
export class RootErrorHandler implements ErrorHandler {
    constructor(public configRef: ConfigRef,
                private panel: Panel,
                private appState: AppState,
                private authState: AuthState,
                @Inject('Window') private window: Window,
                private translateService: TranslateService) {
        this.configRef.configChange.subscribe(config => {
            this.tryInitSentry(config);
        });
    }

    log(msg: string) {
        // do not log in karma
        if (typeof I360_HEADLESS === 'undefined') {
            console.log(msg);
        }
    }

    /**
     * method needed to allow track its call in tests
     * but ravenjs breaks tests down
     */
    tryInitSentry(config) {
        try {
            if (config.ERROR_REPORTING.enable) {
                this.initSentry();
            } else {
                this.terminateSentry();
            }
        } catch (e) {
            // ERROR_REPORTING section is not present
        }
    }
    initSentry() {
        if (I360_HEADLESS || TEST) {
            return;
        }

        if (Sentry.getClient()) {
            if (!this.appState.sentryActive) {
                this.appState.sentryActive = true;
                this.log('SENTRY resumed');
            }
            return;
        }

        Sentry.init({
            dsn: 'https://6369d23c9e9b48a2af3f56c2bd639c24@im360.sentry.cloudlinux.com/18',
            release: VERSION,
            integrations: [
                Sentry.globalHandlersIntegration({
                    onerror: true, // Capture global errors
                    onunhandledrejection: false, // Do not capture unhandled promise rejections
                }),
            ],
            transport: makeBeaconTransport,
            tracesSampleRate: 1,
            ignoreErrors: [noSentryErrorMessage],
            beforeSend: event => {
                if (event.exception?.values?.[0]?.stacktrace?.frames) {
                    event.exception.values[0].stacktrace.frames =
                        event.exception.values[0].stacktrace.frames.map((frame: StackFrame) => ({
                            ...frame,
                            filename: this.fixPaths(frame.filename)
                        }));
                }

                if (event.request && event.request.headers) {
                    for (const key of Object.keys(event.request.headers)) {
                        if (['password', 'chunk'].includes(key.toLowerCase())) {
                            event.request.headers[key] = '[REDACTED]';
                        }
                    }
                }

                if (event.request && event.request.data) {
                    for (const key of Object.keys(event.request.data)) {
                        if (['password', 'chunk'].includes(key.toLowerCase())) {
                            event.request.data[key] = '[REDACTED]';
                        }
                    }
                }

                return event;
            },
            beforeBreadcrumb: breadcrumb => {
                // Filter out XHR breadcrumbs
                if (breadcrumb.category === 'xhr') {
                    return null;
                }
                return breadcrumb;
            },
            initialScope: {
                tags: {
                    role: this.authState.role.value,
                    panel: this.panel.name,
                    lang: this.translateService.currentLang,
                    panelLang: this.translateService.panelLang || this.translateService.currentLang,
                }
            },
            maxBreadcrumbs: 50,
            autoSessionTracking: false
        });

        console.assert(Sentry.getClient() !== undefined, 'Sentry did not setup');
        this.log('SENTRY initialized');
        this.appState.sentryActive = true;
    }

    terminateSentry() {
        if (Sentry.getClient()) {
            Sentry.close()
                .then(() => {
                    this.log('SENTRY terminated');
                    this.appState.sentryActive = false;
                });
        }
    }

    private fixPaths(text?: string): string | undefined {
        // Bundles and sourcemaps are not retrievable for sentry due to cpanel auth and IP
        // restrictions, so we replace paths with the path to UI build server
        if (!text || !VERSION || !RELEASE) {
            return text;
        }
        const uiVersion = `${VERSION}-${RELEASE}`;

        return text.replace(
            /https?:\/\/.*\/imunify(?:360)?\/assets\/static\//g,
            `${URL_FOR_ASSETS}/${uiVersion}/assets/static/`,
        );
    }

    public handleError(error: any): void {
        if (this.panel.isPlesk && !this.window.location.href.includes('imunify')) {
            // skip errors in Plesk, when the plugin is hidden
            return;
        }

        this.log('handle error');
        if (error.noSentry || (error.rejection && error.rejection.noSentry)) {
            return;
        }

        if (TEST || !this.appState.sentryActive) {
            throw error;
        }

        let originalError = error.originalError;
        if (!originalError) {
            if (error.originalStack) {
                // Sometimes "originalError" is missing,
                // but we still have "originalStack" and "message"
                originalError = new Error(error.message);
                originalError.name = error.name || originalError.name;
                originalError.stack = error.originalStack;
            } else {
                originalError = error;
            }
        }
        Sentry.captureException(originalError);
    }

}

function makeBeaconTransport(options: BaseTransportOptions): Transport {
    function makeRequest(request: { body: string }) {
        return new Promise<{ statusCode: number }>(resolve => {
            const success = navigator.sendBeacon(options.url, request.body);
            resolve({
                statusCode: success ? 200 : 0,
            });
        });
    }

    return Sentry.createTransport(options, makeRequest);
}
